import { useCurrentUserId } from '@framework/auth';
import { Navigate } from 'react-router';
import { UserPagePathBuilder } from '../UserPagePathBuilder';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@framework/ui/atoms/Button';
import { Header } from './Header';
import { StopMailRequestConfirmModal } from './StopMailRequestConfirmModal';
import { trpcClient } from '@framework/trpcClient';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

const DistributionStatus = {
  distributing: 'auth.stopMail.status.distributing',
  stopped: 'auth.stopMail.status.stopped',
  willBeFinishedToday: 'auth.stopMail.status.lastToday',
  finished: 'auth.stopMail.status.finished',
} as const satisfies Record<string, ParseKeys>;

type DistributionStatusKey = (typeof DistributionStatus)[keyof typeof DistributionStatus];

/**
 * メール通知停止設定画面
 *
 * @returns
 */
export const StopMailRequestPage: React.FC<unknown> = () => {
  const { t } = useTranslation();
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [status, setStatus] = useState<DistributionStatusKey | null>(null);
  const [stoppable, setStoppable] = useState<boolean>(false);

  const currentUserId = useCurrentUserId();

  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false);
  }, []);

  const handleOnboardingStop = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const stopOnboardingMail = useCallback(async () => {
    try {
      await trpcClient.user.stopOnboardingGuide.mutate();
      location.reload();
    } catch (error) {
      alert(t('common.message.errorOccurred'));
      throw error;
    }
  }, [t]);

  const kind = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('kind') || '';
  }, []);

  useEffect(() => {
    async function fetch() {
      if (currentUserId) {
        const response = await trpcClient.user.fetchOnboardingGuideDistributionStatus.query();
        if (response.ok) {
          setStatus(DistributionStatus[response.status]);
          setStoppable(response.stoppable);
        }
      }
    }
    fetch();
  }, [currentUserId]);

  if (!currentUserId) {
    // ログインは必須（ログイン後に本画面を表示）
    const requestPath = location.pathname + location.search;
    return <Navigate to={UserPagePathBuilder.loginPage(requestPath)} replace />;
  }

  return (
    <>
      <Header currentUserId={currentUserId} />
      {kind === 'onboarding-guide' && (
        <>
          <Contents onOnboardingStop={handleOnboardingStop} status={status} stoppable={stoppable} />
          <StopMailRequestConfirmModal
            isOpen={isConfirmOpen}
            onSubmit={stopOnboardingMail}
            onClose={handleCloseConfirm}
          />
        </>
      )}
    </>
  );
};

type ContentsProps = {
  onOnboardingStop(): void;
  status: DistributionStatusKey | null;
  stoppable: boolean;
};

const Contents: React.FC<ContentsProps> = ({ onOnboardingStop, status, stoppable }: ContentsProps) => {
  const { t } = useTranslation();
  return (
    <div className="p-8">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-bold">{t('auth.stopMail.settings.title')}</h2>
      </div>
      <div className="overflow-y-scroll pb-8" style={{ maxHeight: 'calc(100vh - 138px)' }}>
        <table className="w-full border-separate">
          <thead className="sticky top-0 z-10 bg-white">
            <tr className="text-left">
              <th className="border-y-2 p-2">{t('auth.stopMail.table.kind')}</th>
              <th className="border-y-2 p-2">{t('auth.stopMail.table.status')}</th>
              <th className="border-y-2 p-2" />
            </tr>
          </thead>
          <tbody>
            <tr className="border-b-2">
              <td className="border-b-2 border-gray-400 py-4 align-middle">{t('auth.stopMail.table.onboarding')}</td>
              <td className="border-b-2 border-gray-400 py-4 align-middle">{status ? t(status) : '-'}</td>
              <td className="border-b-2 border-gray-400 py-4 align-middle">
                <Button size="sm" color="brand" onClick={onOnboardingStop} disabled={!stoppable}>
                  {t('auth.stopMail.action.stop')}
                </Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
